import { matchPath, Navigate, Outlet, useLocation } from "react-router-dom";
import { useGlobalState } from "./components/global";
import { Layout, Menu, Button, Modal, Form, message, Input, Avatar } from 'antd';
import { Link } from "react-router-dom";
import * as AntdIcons from '@ant-design/icons';

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useEffect, useMemo, useState } from "react";
import useLocalStorageState from 'use-local-storage-state'

const { Header } = Layout;

function Root() {
    const location = useLocation();
    const [editPasswordForm] = Form.useForm();
    const [currentPermissions, setCurrentPermissions] = useLocalStorageState('sign-in-permission', { defaultValue: [] });
    const { user, permission, navActiveKey, jsonRequest: jsonClient, signOut, setPermission } = useGlobalState();
    const [avatarUrl, setAvatarUrl] = useState<string>('');
    const [flatPermissions, setFlatPermissions] = useState<any[]>([]);
    dayjs.extend(utc)
    dayjs.extend(timezone)
    dayjs.tz.setDefault('UTC')

    const renderMenuIcon = (menu: any) => {
        if (!menu.icon) return undefined;

        // 如果是 Ant Design 图标
        if (menu.iconType === 'antd' && menu.icon) {
            const IconComponent = (AntdIcons as any)[menu.icon];
            // 检查图标组件是否存在并渲染它
            return IconComponent ? <IconComponent /> : undefined;
        }

        // 如果是图片图标
        return <img src={menu.icon} style={{ width: '16px', height: '16px' }} />;
    };

    useEffect(() => {
        if (user?.avatar) {
            init(user.avatar).then(url => {
                setAvatarUrl(url);
            });
        }
    }, [user]);

    const init = async (url: any) => {
        const result = await jsonClient.get(`/common/oss-file-visit-url`, { fileUrl: url })
            .then((resp: any) => resp.json());
        return result?.data;
    };

    useEffect(() => {
        const flattenPermissions = (menus: any[]): any[] => {
            return menus?.reduce((acc, menuItem) => {
                acc.push(menuItem);
                if (menuItem.children && menuItem.children.length > 0) {
                    acc.push(...flattenPermissions(menuItem.children));
                }
                return acc;
            }, [] as any[]) || [];
        };

        setFlatPermissions(flattenPermissions(permission));
    }, [permission]);

    const menuItems = useMemo(() => {
        if (!currentPermissions || !Array.isArray(currentPermissions)) {
            return [];
        }

        return currentPermissions.map((menu: any) => {
            let toPath = menu.path || '#';
            if (menu.children && menu.children.length > 0) {
                const firstAccessibleChild = menu.children.find((child: any) =>
                    child.path && flatPermissions.some((p: any) => p.path === child.path)
                );
                toPath = firstAccessibleChild?.path || '#';
            }
            return (
                <Menu.Item
                    key={menu.path}
                    icon={renderMenuIcon(menu)}
                >
                    <Link to={toPath}>{menu.menuName}</Link>
                </Menu.Item>
            );
        }) || [];
    }, [currentPermissions, flatPermissions]);

    useEffect(() => {
        const fetchMenuTree = async () => {
            try {
                const result = await jsonClient.get(`/back/user/menu-tree`, {})
                    .then((resp) => resp.json());
                const { data } = result;

                // 递归处理菜单树中的图标
                const processMenuTree = async (menus: any[]): Promise<any[]> => {
                    return Promise.all(menus.map(async (item: any) => {
                        const processedItem = { ...item };

                        // 处理图标
                        if (item.icon) {
                            if (item.icon.startsWith('http') || item.icon.includes('oss')) {
                                // 处理图片地址
                                try {
                                    const url = await init(item.icon);
                                    processedItem.icon = url;
                                } catch (error) {
                                    console.error('Failed to process icon URL:', error);
                                }
                            } else {
                                processedItem.iconType = 'antd';
                            }
                        }

                        // 递归处理子菜单
                        if (item.children && item.children.length > 0) {
                            processedItem.children = await processMenuTree(item.children);
                        }

                        return processedItem;
                    }));
                };

                if (data) {
                    const processedData = await processMenuTree(data);
                    handleChangePermissions(processedData);
                }
            } catch (error) {
                console.error('Failed to fetch menu tree:', error);
            }
        };

        fetchMenuTree();
    }, [jsonClient]);

    const handleChangePermissions = (v: any) => {
        setPermission(v)
        setCurrentPermissions(v)
    }

    // 权限检查逻辑
    const checkPermission = () => {
        // 登录和根路径不需要检查权限
        if (location.pathname === "/" || location.pathname === "/sign_in") {
            return true;
        }

        // 扁平化菜单树
        const flattenMenus = (menus: any[]): any[] => {
            return menus.reduce((acc, menu) => {
                acc.push(menu);
                if (menu.children && menu.children.length > 0) {
                    acc.push(...flattenMenus(menu.children));
                }
                return acc;
            }, [] as any[]);
        };

        // 获取所有菜单项
        const flatMenus = flattenMenus(permission);

        // 使用 matchPath 判断是否有匹配的路由
        return flatMenus.some((menu) => matchPath(menu.path, location.pathname));
    };

    if (!user)
        return <Navigate to="/sign_in" />

    if (!checkPermission())
        return <Navigate to="/" />

    const logOut = () => {
        jsonClient.get("/back/user/logout", {})
            .then((response: { json: () => any; }) => response.json())
            .then(({ code, msg }: any) => {
                if (code === 0) {
                    message.success("退出登录成功");
                    signOut();
                } else
                    message.warning(msg)
            })
        return <Navigate to="/sign_in" />
    }

    const changePasswordConfirm = () => {
        const handleEditPasswordSubmit = (values: any, modalClose: () => void) => {
            jsonClient.put("/back/user/modify-pwd", values)
                .then((response: { json: () => any; }) => response.json())
                .then((data: any) => {
                    if (!!data && !!data.success) {
                        message.success({
                            content: <span>密码修改成功，建议<a href="#/" onClick={() => {
                                logOut();
                                message.destroy();
                            }}>重新登录</a></span>, duration: 5
                        })
                        modalClose();
                    } else
                        message.warning(data.msg);
                })
        }

        Modal.confirm({
            title: '修改密码',
            content: <Form form={editPasswordForm} name="basic" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                autoComplete="off">
                <Form.Item label="原密码" name="oldPwd" rules={[{ required: true, message: '原始密码能为空' }]}>
                    <Input.Password />
                </Form.Item>

                <Form.Item label="新密码" name="newPwd" rules={[{ required: true, message: '新密码不能为空' }]}>
                    <Input.Password />
                </Form.Item>
            </Form>,
            onOk: (close: any) => {
                editPasswordForm
                    .validateFields()
                    .then((values: any) => {
                        editPasswordForm.resetFields();
                        handleEditPasswordSubmit(values, close);
                    })
                    .catch((info: any) => {
                        console.log('Validate Failed:', info);
                    });
                return false
            }
        });
    }


    return <div className="App">
        <main role="main" className="container">
            <div style={{ padding: "5px" }}>
                <Layout className="layout">
                    <Header style={{ background: "#fff", padding: 0, display: "flex" }}>
                        <div className="logo">
                        </div>

                        <Menu theme="light" mode="horizontal" selectedKeys={[navActiveKey!]}
                            style={{ border: "none", flexGrow: 1 }}>
                            {menuItems}
                        </Menu>
                        <div className="side">
                            <Avatar src={avatarUrl} />
                            <span style={{ padding: "4px 15px" }}> {user.nickName}</span>
                            <Button type="link" onClick={() => changePasswordConfirm()}>修改密码</Button>
                            <Button type="link" onClick={() => logOut()}>退出</Button>
                        </div>
                    </Header>
                </Layout>
            </div>

            <Outlet />
        </main>
    </div>
}

export default Root;